import { P_WAVE, interpolateShape, pAmplitude, ventricularShape } from './morphology';

// Timing schedules are explicit illustrations of the cited criteria.
// All leads use the same schedule, but the 12 lead sketches are not a patient recording.
export function teachingEvents(id, duration = 10) {
  const events = [];
  if (id === 'vf') return events;
  if (id === 'av3') {
    for (let p = .25; p < duration; p += 2 / 3) events.push({ p });
    for (let q = .58; q < duration; q += 1.5) events.push({ q });
    return events;
  }
  if (id === 'af') {
    const intervals = [.51, .93, .62, .43, .89, .58, .73, 1.02, .46];
    let q = .42;
    let i = 0;
    while (q < duration) { events.push({ q }); q += intervals[i++ % intervals.length]; }
    return events;
  }
  if (id === 'flutter' || id === 'vt') {
    const interval = id === 'flutter' ? .4 : .36;
    for (let q = .42; q < duration; q += interval) events.push({ q });
    return events;
  }
  const interval = id === 'sinus-tachy' ? .5 : id === 'sinus-brady' ? 4 / 3 : .8;
  for (let i = 0, p = .25; p < duration; i += 1, p += interval) {
    if (id === 'pvc' && i === 2) { events.push({ q: 1.66, ectopic: true }); continue; }
    if (id === 'pac' && i >= 2) p = 1.55 + (i - 2) * .8;
    const dropped = ['mobitz1', 'mobitz2'].includes(id) && i % 4 === 3;
    const pr = id === 'av1' ? .30 : id === 'mobitz1' ? [.16, .24, .28, .16][i % 4] : .16;
    events.push({ p, q: dropped ? undefined : p + pr, pr, dropped, premature: id === 'pac' && i === 2 });
  }
  return events;
}

function baseline(id, time, lead) {
  if (id === 'vf') {
    return (.35 + .15 * Math.sin(time * 2.7)) * Math.sin(2 * Math.PI * 4.3 * time + .8 * Math.sin(time * 3.7)) +
      .18 * Math.sin(2 * Math.PI * 6.7 * time + .4) + .09 * Math.sin(2 * Math.PI * 9.2 * time);
  }
  if (id === 'af') return .028 * Math.sin(time * 43) + .02 * Math.sin(time * 61 + 1) + .014 * Math.sin(time * 29);
  if (id === 'flutter') {
    const phase = (time % .2) / .2;
    const tooth = phase < .8 ? .06 - .24 * phase / .8 : -.18 + .24 * (phase - .8) / .2;
    return lead === 'V1' ? -tooth : tooth;
  }
  return 0;
}

export function getWaveform(id, lead, duration, start = 0) {
  const events = teachingEvents(id, start + duration + 1);
  const normalQrs = ventricularShape(id, lead);
  const ectopicQrs = ventricularShape(id, lead, true);
  const count = Math.ceil(duration * 500);
  return Array.from({ length: count + 1 }, (_, index) => {
    const time = start + index * duration / count;
    let value = baseline(id, time, lead);
    events.forEach(event => {
      if (event.p !== undefined) value += interpolateShape(P_WAVE, time - event.p) * pAmplitude(lead) * (event.premature ? 1.7 : 1);
      if (event.q !== undefined) value += interpolateShape(event.ectopic ? ectopicQrs : normalQrs, time - event.q);
    });
    return { time: time - start, value };
  });
}

export function getMarkers(id, duration, start = 0) {
  return teachingEvents(id, duration + start).flatMap(event => {
    if (event.dropped) return [{ from: event.p, to: event.p + .34, label: 'P ohne QRS' }];
    if (['av1', 'mobitz1', 'mobitz2'].includes(id) && event.q !== undefined) {
      return [{ from: event.p, to: event.q, label: `PQ ${Math.round(event.pr * 1000)} ms` }];
    }
    if (id === 'av3') return [{ from: event.p ?? event.q, to: (event.p ?? event.q) + .1, label: event.p !== undefined ? 'P' : 'QRS' }];
    if (event.q === undefined) return [];
    if (id.startsWith('st-')) return [{ from: event.q + .08, to: event.q + .18, label: 'ST' }];
    if (['rbbb', 'lbbb'].includes(id)) return [{ from: event.q, to: event.q + .16, label: 'QRS 160 ms' }];
    if (event.ectopic || event.premature) return [{ from: event.p ?? event.q, to: event.q + .18, label: event.ectopic ? 'VES' : 'SVES' }];
    return [];
  }).filter(mark => mark.from >= start && mark.to <= start + duration)
    .map(mark => ({ ...mark, from: mark.from - start, to: mark.to - start }));
}